// AppShell — sidebar + topbar layout for all authenticated pages
import { Outlet, useLocation, Navigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import DemoBar from "./DemoBar";
import NeedHelpButton from "./NeedHelpButton";
import { useAuth } from "@/context/AuthContext";
import { useView } from "@/context/ViewContext";
import { NotificationProvider } from "@/context/NotificationContext";
import { buildTheme } from "@/lib/theme";
import { loadGoogleFont } from "@/lib/fonts";
import { PageHeaderProvider, usePageHeader } from "@/context/PageHeaderContext";
import { useEffect } from "react";
import { flushAllQueuedPhotos } from "@/lib/photoFlusher";

const TITLES = {
    "/app/dashboard": ["Dashboard", "Overview of clients, projects, compliance and actions"],
    "/app/clients": ["Clients", "Manage client organisations, portals and branding"],
    "/app/buildings": ["Sites & Buildings", "Hierarchy of clients → sites → buildings"],
    "/app/projects": ["Projects", "All compliance, CDM, BSC and audit projects"],
    "/app/actions": ["Actions", "Follow-ups from audits, inspections, incidents and concerns"],
    "/app/users": ["Users & Roles", "Internal team and client portal users"],
    "/app/audit": ["Audit Log", "Every record event — create, view, update, delete"],
    "/app/settings": ["Settings", "Tenant settings, feature flags and integrations"],
};

export default function AppShell() {
    const { user, loading } = useAuth();
    const { portalClient, isInternalView } = useView();
    const location = useLocation();

    const headingFont = !isInternalView ? portalClient?.branding?.heading_font : null;
    const bodyFont = !isInternalView ? portalClient?.branding?.body_font : null;
    useEffect(() => {
        if (headingFont) loadGoogleFont(headingFont);
        if (bodyFont) loadGoogleFont(bodyFont);
    }, [headingFont, bodyFont]);

    // App-level background photo flusher — uploads queued offline photos for ANY
    // record on this device, even when that record's own page isn't open.
    useEffect(() => {
        if (!user) return;
        if (navigator.onLine) flushAllQueuedPhotos();
        const onVisible = () => { if (document.visibilityState === "visible" && navigator.onLine) flushAllQueuedPhotos(); };
        document.addEventListener("visibilitychange", onVisible);
        const onOnline = () => flushAllQueuedPhotos();
        window.addEventListener("online", onOnline);
        const interval = setInterval(() => { if (navigator.onLine) flushAllQueuedPhotos(); }, 20000);
        return () => {
            document.removeEventListener("visibilitychange", onVisible);
            window.removeEventListener("online", onOnline);
            clearInterval(interval);
        };
    }, [user]);

    if (loading) {
        return (
            <div className="min-h-screen bg-[#F5F3F0] grid place-items-center">
                <div className="font-serif-italic text-[#9A968F] text-xl">Loading…</div>
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;

    const routeMeta = TITLES[location.pathname];
    const routeTitle = routeMeta ? routeMeta[0] : "";
    const routeSub = routeMeta ? routeMeta[1] : "";
    const wideLogoUrl = !isInternalView && portalClient?.branding?.wide_logo_url
        ? `${process.env.REACT_APP_BACKEND_URL}/api/clients/${portalClient.id}/wide-logo?mode=${user?.mode || "live"}`
        : null;
    const bannerUrl = !isInternalView && portalClient?.branding?.banner_url
        ? `${process.env.REACT_APP_BACKEND_URL}/api/clients/${portalClient.id}/banner?mode=${user?.mode || "live"}`
        : null;
    const brandBg = !isInternalView ? (portalClient?.branding?.primary_colour || "#C75A1B") : null;

    return (
        <div className="flex min-h-screen"
            data-portal={!isInternalView ? portalClient?.slug : "internal"}
            style={{
                ...buildTheme({ isInternalView, client: portalClient }),
                background: "var(--theme-bg)",
            }}
        >
            <NotificationProvider>
                <PageHeaderProvider>
                    <Sidebar />
                    <ShellMain
                        isInternalView={isInternalView}
                        portalClient={portalClient}
                        routeTitle={routeTitle}
                        routeSub={routeSub}
                        wideLogoUrl={wideLogoUrl}
                        bannerUrl={bannerUrl}
                        brandBg={brandBg}
                    />
                </PageHeaderProvider>
            </NotificationProvider>
            <NeedHelpButton />
        </div>
    );
}

function ShellMain({ isInternalView, portalClient, routeTitle, routeSub, wideLogoUrl, bannerUrl, brandBg }) {
    const { header } = usePageHeader();
    // The page-specific title/subtitle (route default, overridable by a page).
    const pageTitle = header?.title ?? routeTitle;
    const pageSub = header?.subtitle ?? routeSub;

    // Topbar: internal shows the page heading; portal shows the fixed client identity.
    const topTitle = isInternalView ? (pageTitle || "Priority Risk") : `${portalClient?.name} · Portal`;
    const topSub = isInternalView ? pageSub : "Branded client portal · scoped to your sites and reports";

    return (
        <main className="flex-1 min-w-0 flex flex-col">
            <DemoBar />
            <Topbar title={topTitle} subtitle={topSub} branded={!isInternalView}
                logoUrl={wideLogoUrl} bannerUrl={bannerUrl} brandBg={brandBg} />
            <div className="px-7 py-6 pb-16 flex-1 animate-fade-in">
                {!isInternalView && header && header.title && (
                    <div className="mb-6" data-testid="portal-page-heading">
                        <h1 className="text-[22px] font-bold leading-tight text-[#1F1F1F]">{header.title}</h1>
                        {header.subtitle && <p className="text-[13px] mt-0.5" style={{ color: "#6F6A64" }}>{header.subtitle}</p>}
                    </div>
                )}
                <Outlet />
            </div>
        </main>
    );
}
